import { Group, Image, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { SettingsButton } from '@repo/common/components';
import { Button, Text } from '@repo/foundations';
import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../api';
import { REGION_REDIRECT_URL } from '../../../api/hooks/useRegionRedirect';
import { IS_LOCAL_OR_DEVELOPMENT } from '../../../utils/envs';
import {
	authenticateChromeExtension,
	disconnectChromeExtension,
} from '../../../utils/extension';
import { ChromeExtensionLocalDevelopmentModal } from './ChromeExtensionLocalDevelopmentModal';
import {
	CHROME_EXTENSION_ID,
	CHROME_EXTENSION_ID_LOCAL_STORAGE_KEY,
} from './constants';

interface ChromeExtensionRowProps {
	installed: boolean;
	authenticated: boolean;
	version?: string;
	onAuthenticatedChange: (authenticated: boolean) => void;
}

export function ChromeExtensionRow({
	installed,
	authenticated,
	version,
	onAuthenticatedChange,
}: ChromeExtensionRowProps) {
	const [opened, { open, close }] = useDisclosure(false);

	const { domain } = useAuthUser();

	const handleGetSecodaRedirectURL = useCallback(async () => {
		try {
			const response = await axios.get(REGION_REDIRECT_URL, {
				params: { domain },
			});
			return response.data.to;
		} catch {
			// It's possible that data.to does not exist, in which case we return undefined.
			// Undefined instead of null because the next call to authenticate chrome extension
			// expect undefined or a string
			return undefined;
		}
	}, [domain]);

	const handleGetExtension = () => {
		window.open(
			`https://chromewebstore.google.com/detail/secoda/${CHROME_EXTENSION_ID}`,
			'_blank'
		);
	};

	const handleConnect = useCallback(async () => {
		if (IS_LOCAL_OR_DEVELOPMENT) {
			open();
		} else {
			try {
				const redirectURL = await handleGetSecodaRedirectURL();
				const response = await authenticateChromeExtension(redirectURL);
				onAuthenticatedChange(response.success);

				if (response.success) {
					showNotification({
						message: 'Successfully connected',
						color: 'green',
					});
				} else {
					showNotification({
						message: `Failed to connect. ${
							'message' in response ? response.message : 'Unknown error'
						}`,
						color: 'red',
					});
				}
			} catch (error) {
				onAuthenticatedChange(false);
				showNotification({
					message: `Something went wrong. Failed to connect`,
					color: 'red',
				});
			}
		}
	}, [open, handleGetSecodaRedirectURL, onAuthenticatedChange]);

	const handleDisconnect = useCallback(async () => {
		const response = await disconnectChromeExtension();
		localStorage.removeItem(CHROME_EXTENSION_ID_LOCAL_STORAGE_KEY);
		onAuthenticatedChange(!response.success);
		if (response.success) {
			showNotification({
				message: 'Successfully disconnected',
				color: 'green',
			});
		} else {
			showNotification({
				message: `Failed to connect. ${
					'message' in response ? response.message : 'Unknown error'
				}`,
				color: 'red',
			});
		}
	}, [onAuthenticatedChange]);

	const showGetExtensionButton = !installed && !IS_LOCAL_OR_DEVELOPMENT;
	const showConnectButton = !authenticated;

	const button = useMemo(() => {
		if (showGetExtensionButton) {
			return (
				<Button variant="primary" onClick={handleGetExtension}>
					Get extension
				</Button>
			);
		}
		if (showConnectButton) {
			return (
				<Button variant="default" onClick={handleConnect}>
					Connect
				</Button>
			);
		}

		return (
			<Menu position="bottom-start">
				<Menu.Target>
					<Button
						variant="tertiary"
						leftIconName="pointFilled"
						iconColor="icon/primary/default"
						rightIconName="chevronDown"
						leftIconFilled
					>
						Connected
					</Button>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item onClick={handleDisconnect}>Disconnect</Menu.Item>
				</Menu.Dropdown>
			</Menu>
		);
	}, [
		showGetExtensionButton,
		showConnectButton,
		handleDisconnect,
		handleConnect,
	]);

	return (
		<>
			<SettingsButton
				leftComponent={
					<Image src="/images/logos/chrome.svg" width={16} height={16} />
				}
				title="Google Chrome extension"
				description={
					<Group>
						{version && (
							<Text size="sm" color="text/secondary/default">
								{version}
							</Text>
						)}
					</Group>
				}
			>
				{button}
			</SettingsButton>
			<ChromeExtensionLocalDevelopmentModal
				opened={opened}
				onClose={close}
				onAuthenticatedChange={onAuthenticatedChange}
			/>
		</>
	);
}
